import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { haptic } from '../lib/telegram';

/*
  Примитивы интерфейса.

  Два правила, которым подчинено всё ниже:
   - отклик появляется в момент нажатия, а не после отпускания;
   - цвета берутся только из темы Telegram, своей палитры нет.
*/

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--color-accent)] text-[var(--color-on-accent)]',
  secondary: 'bg-[var(--color-surface)] text-[var(--color-text)]',
  ghost: 'bg-transparent text-[var(--color-link)]',
  danger: 'bg-transparent text-[var(--color-danger)]',
};

export function Button({
  variant = 'primary',
  loading = false,
  children,
  className = '',
  onClick,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      onClick={(event) => {
        haptic.tap();
        onClick?.(event);
      }}
      className={`pressable t-callout inline-flex min-h-[46px] items-center justify-center gap-2 rounded-[var(--radius-control)] px-4 font-medium disabled:pointer-events-none disabled:opacity-35 ${BUTTON_STYLES[variant]} ${className}`}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
}

export const Spinner = ({ size = 18 }: { size?: number }) => (
  <span
    aria-label="Загрузка"
    role="status"
    style={{ width: size, height: size, borderWidth: Math.max(2, Math.round(size / 9)) }}
    className="inline-block animate-spin rounded-full border-current border-t-transparent opacity-50"
  />
);

export const Card = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div className={`rounded-[var(--radius-card)] bg-[var(--color-section)] p-4 ${className}`}>
    {children}
  </div>
);

/** Группа настроек: заголовок задаёт смысл, карточка — границы. */
export function Section({
  title,
  action,
  footer,
  children,
}: {
  title?: string;
  action?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-6">
      {(title || action) && (
        <header className="mb-2 flex items-end justify-between gap-3 px-4">
          {title && <h2 className="t-group text-[var(--color-hint)]">{title}</h2>}
          {action}
        </header>
      )}
      {children}
      {footer && <p className="t-footnote mt-2 px-4 text-[var(--color-hint)]">{footer}</p>}
    </section>
  );
}

/** Сгруппированный список: разделители между строками, но не по краям. */
export const List = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div
    className={`overflow-hidden rounded-[var(--radius-card)] bg-[var(--color-section)] ${className}`}
  >
    <div className="divide-y divide-[var(--color-separator)]">{children}</div>
  </div>
);

export function Row({
  title,
  subtitle,
  trailing,
  onClick,
  chevron = false,
  destructive = false,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  destructive?: boolean;
}) {
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span
          className="t-body block"
          style={destructive ? { color: 'var(--color-danger)' } : undefined}
        >
          {title}
        </span>
        {subtitle && <span className="t-footnote mt-0.5 block text-[var(--color-hint)]">{subtitle}</span>}
      </span>
      {trailing && <span className="t-subhead shrink-0 text-[var(--color-hint)]">{trailing}</span>}
      {chevron && (
        <svg width="8" height="13" viewBox="0 0 8 13" aria-hidden="true" className="shrink-0 opacity-30">
          <path
            d="M1.5 1.5L6.5 6.5L1.5 11.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </>
  );

  if (!onClick) {
    return <div className="flex items-center gap-3 px-4 py-3">{content}</div>;
  }

  return (
    <button
      onClick={() => {
        haptic.select();
        onClick();
      }}
      className="pressable-row flex w-full items-center gap-3 px-4 py-3 text-left"
    >
      {content}
    </button>
  );
}

export const Input = ({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...rest}
    className={`t-body min-h-[46px] w-full rounded-[var(--radius-control)] bg-[var(--color-surface)] px-3.5 text-[var(--color-text)] outline-none placeholder:text-[var(--color-hint)] focus:ring-2 focus:ring-[var(--color-accent)] ${className}`}
  />
);

export const Textarea = ({ className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...rest}
    className={`t-body w-full resize-none rounded-[var(--radius-control)] bg-[var(--color-surface)] p-3.5 text-[var(--color-text)] outline-none placeholder:text-[var(--color-hint)] focus:ring-2 focus:ring-[var(--color-accent)] ${className}`}
  />
);

/**
 * Переключатель сам по себе, без подписи.
 *
 * Нужен там, где строка уже занята содержимым — например, в списке филиалов.
 * Подпись всё равно обязательна: она невидима, но озвучивается скринридером,
 * иначе переключатель для него безымянный.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <span className={`relative shrink-0 ${disabled ? 'opacity-40' : ''}`}>
      <input
        type="checkbox"
        aria-label={label}
        className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          haptic.select();
          onChange(event.target.checked);
        }}
      />
      <span className="block h-[31px] w-[51px] rounded-full bg-[var(--color-separator)] transition-colors duration-200 peer-checked:bg-[var(--color-positive)]" />
      <span className="absolute top-[2px] left-[2px] h-[27px] w-[27px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-transform duration-200 [transition-timing-function:var(--ease-out)] peer-checked:translate-x-5" />
    </span>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3">
      <span className="min-w-0">
        <span className="t-body block">{label}</span>
        {description && (
          <span className="t-footnote mt-0.5 block text-[var(--color-hint)]">{description}</span>
        )}
      </span>
      <Switch checked={checked} onChange={onChange} label={label} />
    </label>
  );
}

/** Сегментированный переключатель: выбор одного из нескольких равных вариантов. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-[var(--radius-control)] bg-[var(--color-surface)] p-1">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            onClick={() => {
              haptic.select();
              onChange(option.value);
            }}
            aria-pressed={active}
            className="t-subhead flex-1 rounded-[9px] py-1.5 font-medium transition-all duration-200 [transition-timing-function:var(--ease-out)]"
            style={
              active
                ? {
                    background: 'var(--color-section)',
                    color: 'var(--color-text)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  }
                : { color: 'var(--color-hint)' }
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Stars({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span className="inline-flex gap-[1px]" aria-label={`Оценка ${rating} из 5`}>
      {[1, 2, 3, 4, 5].map((index) => (
        <svg key={index} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
          <path
            d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9 4.8 17.6l1-5.8L1.5 7.7l5.9-.9z"
            fill={index <= rating ? '#ffb302' : 'var(--color-separator)'}
          />
        </svg>
      ))}
    </span>
  );
}

export function Badge({ children, color }: { children: ReactNode; color?: string }) {
  const tone = color ?? 'var(--color-hint)';
  return (
    <span
      className="t-caption inline-flex items-center rounded-full px-2 py-[3px] font-medium whitespace-nowrap"
      style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="surface-in flex flex-col items-center px-8 py-16 text-center">
      <div className="mb-4 text-[44px] leading-none" aria-hidden="true">
        {icon}
      </div>
      <h3 className="t-title mb-2">{title}</h3>
      <p className="t-subhead mb-6 max-w-[280px] text-[var(--color-hint)]">{description}</p>
      {action}
    </div>
  );
}

export const Skeleton = ({ className = '' }: { className?: string }) => (
  <div
    className={`animate-pulse rounded-[var(--radius-card)] bg-[var(--color-section)] ${className}`}
  />
);

/** Крупный заголовок экрана. Отвечает на вопрос «где я». */
export function ScreenTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <header className="px-4 pt-1 pb-4">
      <h1 className="t-large-title">{title}</h1>
      {subtitle && <p className="t-subhead mt-1 text-[var(--color-hint)]">{subtitle}</p>}
    </header>
  );
}
